// The console's organizations (A19U2), owned by hand: search, plan and demo filters in the address, as the route key's
// arguments; any platform role reads it (the layout lets in only staff).
import { ids } from '@orca/api-client/testIds'
import { createFileRoute } from '@tanstack/react-router'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { PageHeader } from '../../../components/ui/page-header'
import { ConsoleOrgs } from '../../../features/platform/ConsoleOrgs'
import { consoleOrgsQuery } from '../../../features/platform/queries'
import type { ConsoleOrgFilters } from '../../../shared/query/keys'
import { text } from '../../../shared/routing/search'
import '../../../shared/shell/strings'

type Search = { q?: string; plan?: string; demo?: string }

const filtersOf = (search: Search): ConsoleOrgFilters => ({
  q: search.q,
  plan: search.plan,
  demo: search.demo === 'true' ? true : undefined,
})

export const Route = createFileRoute('/platform/orgs/')({
  staticData: { screenId: 'ConsoleOrgsScreen', access: 'platform.org.read' },
  // The route key's query arguments (screens.json `args`), as the apps' LinkResolver reads them;
  // unknown keys are dropped.
  validateSearch: (search: Record<string, unknown>): Search => ({
    q: text(search.q),
    plan: text(search.plan),
    demo: text(search.demo),
  }),
  loaderDeps: ({ search }) => filtersOf(search),
  loader: ({ context: { queryClient }, deps }) => queryClient.ensureQueryData(consoleOrgsQuery(deps)),
  component: ConsoleOrgsScreen,
})

function ConsoleOrgsScreen() {
  const filters = Route.useLoaderDeps()
  const navigate = Route.useNavigate()
  const onFilters = useCallback(
    (next: ConsoleOrgFilters) =>
      navigate({
        search: { q: next.q, plan: next.plan, demo: next.demo ? 'true' : undefined },
        replace: true,
      }),
    [navigate],
  )
  const onOpen = useCallback(
    (orgId: string) => navigate({ to: '/platform/orgs/$orgId', params: { orgId } }),
    [navigate],
  )
  const { t } = useTranslation()
  return (
    <main data-testid={ids.consoleOrgs.root} className="flex flex-col gap-(--stack-lg)">
      <PageHeader title={t('shell.nav.orgs')} />
      <ConsoleOrgs filters={filters} onFilters={onFilters} onOpen={onOpen} />
    </main>
  )
}
